import { entries, flatMap, flow, groupBy, map, sumBy } from 'lodash/fp';
import {
  Assignment,
  Classroom,
  Group,
  Student,
  StudentProgress,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { CharacterProgress } from 'state/providers/ReportsProvider/types';
import { ActivityTypes } from '@monorepo/shared/src/types';

const getBookReadStudentCount = (characterProgress: CharacterProgress[]) => {
  // Pretty sure this is enough - items in this array _should_ never have duplicate students.
  return characterProgress.filter((cp) => cp.bookReadCount > 0).length;
};

export const getCharactersProgress = (
  students: Student[],
  progressData: StudentProgress[] = []
): CharacterProgress[] =>
  flow(
    flatMap('characterProgress'), // Collect all character progress from each student
    groupBy('characterId'),
    entries,
    map(([characterId, characterProgressItems]) => {
      return {
        characterId: +characterId,
        activityCompletedCount: sumBy('activityCompletedCount')(characterProgressItems),
        activityTotalCount: sumBy('activityTotalCount')(characterProgressItems),
        bookReadStudentCount: getBookReadStudentCount(characterProgressItems),
        bookReadCount: sumBy('bookReadCount')(characterProgressItems),
        activitySkippedCount: sumBy('activitySkippedCount')(characterProgressItems),
      };
    })
  )(progressData);

export const getStudentGroup = (classroom: Classroom, studentId: number): Group | undefined => {
  return classroom.groups?.find((group) =>
    group.groupEnrollments.some((enrollment) => enrollment?.studentId === studentId)
  );
};

export const getSelectedGroups = (classroom: Classroom, studentIds: number[]) => {
  const groups = classroom.groups ?? [];

  const selectedGroups = groups.filter((group) => {
    return (
      group.groupEnrollments.length &&
      group.groupEnrollments.every((g) => studentIds.includes(g?.student?.id ?? 0))
    );
  });

  return selectedGroups ?? [];
};

export const getSelectedStudents = (classroom: Classroom, studentIds: number[]) => {
  const selectedStudents =
    studentIds?.map((studentId) =>
      classroom?.students?.find((student) => student.id === studentId)
    ) ?? [];
  const selectedGroups = getSelectedGroups(classroom, studentIds);

  return selectedStudents.filter((student) => {
    return !selectedGroups?.find((group) => {
      return group?.groupEnrollments?.find((groupEnrollment) => {
        return groupEnrollment?.studentId === student?.id;
      });
    });
  });
};

export const getActivityAssignment = (
  assignments: Assignment[],
  char: string | undefined,
  activityType: ActivityTypes
) => {
  return assignments
    .find(({ character }) => character.character === char)
    ?.assignmentActivities.find(({ activity }) => activity?.activityType?.name === activityType);
};

export const getAttemptFiles = ({
  assignments,
  studentId,
  char,
  activityType,
}: {
  assignments: Assignment[];
  studentId: number | undefined;
  char: string | undefined;
  activityType: ActivityTypes;
}) => {
  if (!assignments?.length || !studentId || !char) return null;
  return getActivityAssignment(assignments, char, activityType)?.attempts?.find(
    (a) => a.studentId === studentId
  )?.files;
};

export const getProgressDataForStudentAndLetter = (progressData, studentId, characterId) => {
  return progressData
    ?.find(({ student }) => student.id === studentId)
    ?.characterProgress?.find((p) => p.characterId === characterId);
};
